import { css } from '@emotion/react';
import { hexToRgba } from '~/utilities/helpers';
import { label as labelFont, error as errorFont } from '~/styles/typography';

export const formElement = ({ theme }) => css`
    display: flex;
    position: relative;
    flex-direction: row;

    &:focus-within {
        outline: 3px solid lightBlue;
        outline-offset: 5px;

        input,
        button[type='submit'] {
            box-shadow: none;
            outline: none;
        }
    }
`;

export const fieldWrapper = () => css`
    display: flex;
    flex-direction: column;
    position: relative;
`;

export const input = ({
    theme,
    size = 'normal',
    error,
    reverse,
    emphasis = false
}) => {
    const height = size === 'normal' ? '45px' : '75px';

    return css`
        font-size: 1.6rem;
        font-family: ${theme.fonts.heading};
        font-weight: ${theme.fontWeights.medium};
        color: ${theme.colors.text.black};
        background-color: transparent;
        border: 1px solid ${theme.colors.primary.gray};
        width: 100%;
        padding: 12px 15px;

        height: calc(${height} * 0.85);

        @media (min-width: ${theme.breakpoint.largeTablet}) {
            height: ${height};
            font-size: 1.7rem;
        }

        &::placeholder {
            opacity: 0.6;
        }

        &:focus {
            ${inputFocus({ theme })};
        }

        ${error && inputError({ theme })};

        ${reverse && reverseInput({ theme })};

        ${emphasis && `border-right: 0;`};

        &:disabled {
            opacity: 0.5;
            pointer-events: none;
        }
    `;
};

const reverseInput = ({ theme }) => css`
    border-color: ${theme.colors.primary.yellow};
    color: ${theme.colors.primary.white};

    &::placeholder {
        color: ${theme.colors.primary.white};
        opacity: 1;
    }
`;

export const checkbox = ({ theme }) => css`
    -webkit-appearance: none;
    appearance: none;
    margin: 0;

    font: inherit;
    color: currentColor;
    width: 2.7rem;
    height: 2.7rem;
    flex: 0 0 2.7rem;
    border: 1px solid ${theme.colors.text.black};

    display: grid;
    place-content: center;

    &:checked:before {
        opacity: 1;
    }

    &:before {
        content: '';
        background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 19.87 15'%3E%3Cpath fill='%230654B3' stroke='%230654B3' stroke-width='2' d='M18.46 1.38a1.27 1.27 0 0 0-1.8 0l-9.48 9.48L3.2 6.87a1.3 1.3 0 1 0-1.8 1.87l4.88 4.88a1.27 1.27 0 0 0 1.8 0L18.47 3.25a1.27 1.27 0 0 0 0-1.87Z'/%3E%3C/svg%3E");
        width: 17px;
        height: 13px;
        opacity: 0;
    }

    &:focus {
        ${inputFocus({ theme })};
    }
`;

export const radio = ({ theme }) => css`
    -webkit-appearance: none;
    appearance: none;
    background-color: var(--form-background);
    margin: 0;

    font: inherit;
    color: currentColor;
    width: 2.7rem;
    height: 2.7rem;
    border: 1px solid ${theme.colors.text.black};
    border-radius: 100px;

    display: grid;
    place-content: center;

    &:checked:before {
        opacity: 1;
    }

    &:before {
        content: '';
        background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 17 17'%3E%3Crect width='17' height='17' x='172' y='1971' fill='%230654B3' fill-rule='evenodd' rx='8.5' transform='translate(-172 -1971)'/%3E%3C/svg%3E");
        width: 17px;
        height: 17px;
        opacity: 0;
    }

    &:focus {
        ${inputFocus({ theme })};
    }
`;

export const textarea = ({ theme, error }) => {
    const height = '120px';

    return css`
        ${input({ theme, error })};
        height: calc(${height} * 0.85);

        @media (min-width: ${theme.breakpoint.tablet}) {
            height: ${height};
        }
    `;
};

export const inputError = ({ theme }) => css`
    &:focus,
    &:focus-visible {
        box-shadow: 0 2px 4px 3px ${hexToRgba(theme.colors.text.red, 0.2)};
        border-color: ${theme.colors.text.red};
    }

    border: 1px solid ${theme.colors.text.red};
`;

export const inputFocus = () => css`
    outline: 0;
    box-shadow: 0 2px 4px 3px rgba(0, 0, 0, 0.2);
`;

export const label = ({ theme, size = 'regular' }) => css`
    ${labelFont({ theme, size })};
    margin-bottom: 8px;
`;

export const fieldError = ({ theme }) => css`
    ${errorFont({ theme })};
    color: ${theme.colors.text.red};
    position: absolute;
    top: calc(100% + 5px);
    left: 0;
`;

export const selectContainer = ({ theme }) => css`
    position: relative;

    &::after {
        content: '';
        position: absolute;
        height: 12px;
        width: 22px;
        top: 50%;
        right: 15px;
        pointer-events: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14.92 14.92'%3E%3Cpath fill='none' stroke='%231E2226' stroke-linecap='round' stroke-width='3' d='M12.8 7.36 7.36 12.8 2.12 7.56'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center;
        transform: translateY(-50%);
        transition: transform 0.3s ease;
    }

    &:focus-within {
        &::after {
            transform: translateY(-50%) rotate(180deg);
        }
    }
`;

export const select = ({ ...rest }) => css`
    ${input({ ...rest })};
    appearance: none;
`;
